import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Form,
  Input,
  List,
  Modal,
  Select,
  Space,
  Typography,
  message,
} from "antd";
import dayjs from "dayjs";
import api from "../../api/axios";
import { useNotificationsStatus } from "../../context/NotificationsContext";

type NotificationItem = {
  id: string | number;
  title?: string;
  body?: string;
  type?: string;
  created_at?: string;
  created_At?: string;
};

type Props = {
  showCreate: boolean;
};

export function NotificationsListPage({ showCreate }: Props) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [selected, setSelected] = useState<NotificationItem | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [form] = Form.useForm();
  const { clearHasNew, refreshHasNew } = useNotificationsStatus();

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const tsA = dayjs(a.created_at).valueOf();
      const tsB = dayjs(b.created_at).valueOf();
      return tsB - tsA;
    });
  }, [items]);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const res = await api.get("/pos/notifications", {
        params: { limit: 50 },
      });
      const raw = Array.isArray(res.data?.items) ? res.data.items : [];
      const list = raw.map((item: NotificationItem) => ({
        ...item,
        created_at: item.created_at ?? item.created_At,
      }));

      setItems(list);
      if (list.length) {
        const newest = [...list].sort(
          (a, b) =>
            dayjs(b.created_at).valueOf() - dayjs(a.created_at).valueOf()
        )[0];
        setSelected(newest);
      } else {
        setSelected(null);
      }
    } catch (e: any) {
      message.error(e?.displayMessage || "Failed to load notifications");
      setItems([]);
      setSelected(null);
    } finally {
      setLoading(false);
    }
  };

  const markSeen = async () => {
    try {
      await api.post("/pos/notifications/seen");
    } catch {
      // ignore
    } finally {
      clearHasNew();
    }
  };

  useEffect(() => {
    const run = async () => {
      await markSeen();
      await loadNotifications();
      await refreshHasNew();
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = async (values: any) => {
    const payload = {
      title: (values.title || "").trim(),
      body: (values.body || "").trim(),
      type: (values.type || "").trim(),
    };

    if (!payload.title || !payload.body) {
      message.error("Title and body are required");
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/admin/notifications", payload);
      message.success("Notification created");
      form.resetFields();
      await loadNotifications();
      await refreshHasNew();
    } catch (e: any) {
      message.error(e?.displayMessage || "Failed to create notification");
    } finally {
      setSubmitting(false);
    }
  };

  const fmtDate = (value?: string) => {
    if (!value) return "-";
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm") : "-";
  };

  const openDetail = (item: NotificationItem) => {
    setSelected(item);
    setDetailOpen(true);
  };

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Typography.Title level={3} style={{ margin: 0 }}>
        Notifications
      </Typography.Title>

      {showCreate && (
        <Card title="Create Notification">
          <Form
            layout="vertical"
            form={form}
            onFinish={handleCreate}
            initialValues={{ type: "info" }}
          >
            <Form.Item
              label="Title"
              name="title"
              rules={[{ required: true, message: "Title is required" }]}
            >
              <Input placeholder="Maintenance window" maxLength={150} />
            </Form.Item>

            <Form.Item
              label="Body"
              name="body"
              rules={[{ required: true, message: "Body is required" }]}
            >
              <Input.TextArea
                rows={4}
                showCount
                maxLength={800}
                placeholder="Full message users will read"
              />
            </Form.Item>

            <Form.Item label="Type" name="type">
              <Select
                options={[
                  { value: "info", label: "Info" },
                  { value: "maintenance", label: "Maintenance" },
                  { value: "pricing", label: "Pricing" },
                  { value: "warning", label: "Warning" },
                ]}
              />
            </Form.Item>

            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              disabled={submitting}
            >
              Create Notification
            </Button>
          </Form>
        </Card>
      )}

      <Card title="All Notifications" bodyStyle={{ padding: 0 }}>
        <List
          loading={loading}
          dataSource={sortedItems}
          rowKey={(item) => String(item.id)}
          locale={{ emptyText: "No notifications" }}
          renderItem={(item) => {
            const active = selected?.id === item.id;
            return (
              <List.Item
                onClick={() => openDetail(item)}
                style={{
                  cursor: "pointer",
                  background: active ? "#e6f4ff" : "transparent",
                  borderLeft: active ? "3px solid #1677ff" : "3px solid transparent",
                  transition: "background 0.2s",
                  padding: "16px 20px",
                }}
              >
                <div style={{ width: "100%" }}>
                  <Typography.Text
                    strong
                    style={{ display: "block", textAlign: "left" }}
                  >
                    {item.title || "Untitled"}
                  </Typography.Text>

                  {item.type && (
                    <Badge
                      color="#1677ff"
                      text={item.type}
                      style={{ width: "fit-content", marginTop: 8 }}
                    />
                  )}

                  <Typography.Text
                    type="secondary"
                    style={{ display: "block", textAlign: "right", marginTop: 8 }}
                  >
                    {fmtDate(item.created_at)}
                  </Typography.Text>
                </div>
              </List.Item>
            );
          }}
        />
      </Card>

      <Modal
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={null}
        title="Notification"
      >
        {selected && (
          <Space direction="vertical" size={12} style={{ width: "100%" }}>
            <Typography.Text
              strong
              style={{ display: "block", textAlign: "center" }}
            >
              {selected.title || "Untitled"}
            </Typography.Text>
            {selected.type && <Badge color="#1677ff" text={selected.type} />}
            <Typography.Paragraph style={{ whiteSpace: "pre-wrap" }}>
              {selected.body || "No content"}
            </Typography.Paragraph>
            <Typography.Text
              type="secondary"
              style={{ display: "block", textAlign: "right" }}
            >
              {fmtDate(selected.created_at)}
            </Typography.Text>
          </Space>
        )}
      </Modal>
    </Space>
  );
}
